import { TransactionState } from '__generated__/athena/graphql';

/**
 * Transaction states that mean a purchase offer is no longer active. Shared so
 * callers don't each re-declare (and drift on) the canceled-state set.
 */
export const CANCELED_OFFER_STATES: readonly TransactionState[] = [
  TransactionState.CANCELED,
  TransactionState.BUYER_WALKED,
  TransactionState.REJECTED,
  TransactionState.CONTRACT_FELL_THROUGH,
  TransactionState.BACKUP_EXPIRED,
  TransactionState.UNKNOWN,
];

export const isOfferCanceled = (state: TransactionState | null | undefined): boolean =>
  !!state && CANCELED_OFFER_STATES.includes(state);

/**
 * Transaction states that keep a purchase offer out of the account navigation's
 * "active transactions" list: the terminal canceled states plus CLOSED. A CLOSED
 * offer is a *completed* purchase, so surfacing it as an active "Buy - <street>"
 * entry would send the customer back into a finished purchase flow. This is
 * intentionally broader than the canceled-only set, which other callers rely on
 * to mean "canceled" specifically.
 */
export const INACTIVE_NAV_OFFER_STATES: readonly TransactionState[] = [
  ...CANCELED_OFFER_STATES,
  TransactionState.CLOSED,
];

/**
 * Whether a purchase offer should appear as an active transaction in the account
 * navigation. A missing state is treated as inactive so offers we can't classify
 * are never surfaced as active buys.
 */
export const isActiveBuyerOffer = (state: TransactionState | null | undefined): boolean =>
  !!state && !INACTIVE_NAV_OFFER_STATES.includes(state);

// Transaction states that indicate the home is in contract.
export const IN_CONTRACT_STATES: readonly TransactionState[] = [
  TransactionState.IN_CONTRACT,
  TransactionState.CONTINGENCIES_RELEASED,
];

export const isInContract = (state: TransactionState | null | undefined): boolean =>
  !!state && IN_CONTRACT_STATES.includes(state);

// Transaction states that indicate the home is in contract or has closed.
export const IN_CONTRACT_OR_CLOSED_STATES: readonly TransactionState[] = [
  ...IN_CONTRACT_STATES,
  TransactionState.CLOSED,
];

export const isInContractOrClosed = (state: TransactionState | null | undefined): boolean =>
  !!state && IN_CONTRACT_OR_CLOSED_STATES.includes(state);

export interface TaskGroupAvailability {
  disabled: boolean;
  reason?: string;
}

/**
 * Whether a purchase task group is actionable for the given transaction state.
 * Some groups (welcome-home, escrow & closing) are surfaced by the API before
 * the buyer can act on them, so callers that count or advertise tasks must gate
 * on this rather than treating every returned group as available.
 */
export const getTaskGroupDisabledState = (
  groupTitle: string,
  transactionState: TransactionState | null | undefined,
): TaskGroupAvailability => {
  // All task groups are disabled when the offer is canceled.
  if (isOfferCanceled(transactionState)) {
    return { disabled: true, reason: 'Offer is no longer active' };
  }

  const normalizedTitle = groupTitle.toLowerCase();

  // Welcome Home group: only enabled once the transaction is CLOSED.
  if (normalizedTitle.includes('welcome home')) {
    if (transactionState === TransactionState.CLOSED) {
      return { disabled: false };
    }
    return { disabled: true, reason: 'Available after closing' };
  }

  // All other task groups are enabled when the transaction is closed.
  if (transactionState === TransactionState.CLOSED) {
    return { disabled: false };
  }

  // Combined Escrow & Closing group: enabled when in contract or beyond. This
  // handles both the combined "escrow & closing" group and legacy separate ones.
  if (normalizedTitle.includes('escrow') || normalizedTitle.includes('closing')) {
    if (isInContractOrClosed(transactionState)) {
      return { disabled: false };
    }
    return { disabled: true, reason: 'Available when in contract' };
  }

  return { disabled: false };
};
